import { HelpContent } from './HelpContent';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { findArticle, openHelp, type HelpContext } from './articles';
import './help.css';
import { usesDesignSystem } from '../design-v2/rollout';
import { parseHash } from '../../lib/router';
import * as store from '../../store/ui';

export function HelpButton({ article, label = '?', context }: { article: string; label?: string; context?: HelpContext }) {
  return <button type="button" className="help-trigger" aria-label={`Help: ${findArticle(article)?.title || article}`} onClick={() => openHelp(article, context)}>{label}</button>;
}
export function HelpPanel() {
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState<string | undefined>();
  const [context, setContext] = useState<HelpContext | undefined>();
  const [query, setQuery] = useState('');
  const panel = useRef<HTMLElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const search = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const receive = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.id && !findArticle(detail.id)) return;
      returnFocus.current = document.activeElement as HTMLElement;
      setSelected(detail?.id); setContext(detail?.context); setQuery(''); setVisible(true);
    };
    const click = (event: MouseEvent) => {
      const button = (event.target as Element)?.closest?.<HTMLElement>('[data-help-article]');
      if (!button) return;
      event.preventDefault(); event.stopPropagation(); openHelp(button.dataset.helpArticle, { title: button.dataset.helpTitle, description: button.dataset.helpDescription });
    };
    window.addEventListener('nodes:help', receive);
    document.addEventListener('click', click);
    const navigate = () => setVisible(false);
    window.addEventListener('hashchange', navigate);
    return () => { window.removeEventListener('nodes:help', receive); document.removeEventListener('click', click); window.removeEventListener('hashchange', navigate); };
  }, []);
  useEffect(() => {
    if (!visible) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    search.current?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setVisible(false); }
      if (event.key !== 'Tab') return;
      const controls = panel.current?.querySelectorAll<HTMLElement>('button, input, a[href]');
      if (!controls?.length) return;
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', keydown, true);
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener('keydown', keydown, true); returnFocus.current?.focus(); };
  }, [visible]);
  if (!visible) return null;
  const choose = (id: string) => { setSelected(id); setContext(undefined); setQuery(''); panel.current?.querySelector('.help-body')?.scrollTo(0, 0); };
  const designSystem = usesDesignSystem(parseHash());
  return createPortal(<div className={`help-overlay${designSystem ? ' nodex-v2 v2-help-surface' : ''}`} data-theme={designSystem ? (store.get().theme === 'dark' ? 'dark' : 'light') : undefined} onClick={() => setVisible(false)}>
    <section className="help-panel" ref={panel} role="dialog" aria-modal="true" aria-labelledby="help-title" onClick={event => event.stopPropagation()}>
      <header className="help-header"><h2 id="help-title">Help · Quick guide</h2><button className="help-trigger" onClick={() => setVisible(false)} aria-label="Close help">×</button></header>
      <label className="help-search">Find an answer<input ref={search} type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="For example: project, animation, download" /></label>
      <HelpContent selected={selected} query={query} context={context} choose={choose} onBack={()=>{setSelected(undefined);setContext(undefined);}}/>
    </section>
  </div>, document.body);
}
